/**
 * Ancient + Brave — Navigation Interaction Tracking
 *
 * Pushes a structured `nav_interaction` event to the Elevar data layer for every
 * click on the main navigation, using event delegation rather than per-element
 * bindings. This avoids needing to hand-instrument every link/button across the
 * nav-item-*.liquid templates, which generate variable numbers of links from
 * theme editor settings and linklists.
 *
 * Event shape pushed to window.ElevarDataLayer:
 * {
 *   event: 'nav_interaction',
 *   nav_interaction: {
 *     label: string,         // visible text of the clicked menu item
 *     interaction_type: 'click' | 'submenu_open' | 'submenu_close',
 *     level: 'L1' | 'L2' | 'L3' | 'L4' | null,
 *     origin: 'mega_menu_panel' | 'nav_merchandising' | 'top_bar_toggle' | 'nav_utility',
 *     target_type: 'link' | 'button'
 *   }
 * }
 *
 * NOTE: Confirm the exact global variable name Elevar expects in this install
 * before relying on this in production. window.ElevarDataLayer is used below
 * as a placeholder — swap for the correct global if different.
 */
(function () {
  'use strict';

  var DATA_LAYER_KEY = 'ElevarDataLayer';

  function pushEvent(payload) {
    window[DATA_LAYER_KEY] = window[DATA_LAYER_KEY] || [];
    window[DATA_LAYER_KEY].push({
      event: 'nav_interaction',
      nav_interaction: payload
    });
  }

  /**
   * Returns the visible text of an element, stripping out nested icon/sup
   * markup (e.g. the "V" vegan badge, "New" superscript, arrow spans) so the
   * label is just the clean menu item name.
   */
  function getCleanLabel(el) {
    var clone = el.cloneNode(true);
    // .review_stars and .description are only present on bestseller minicards
    // (blocks/nav-bestsellers.liquid, nav-content-bestsellers.liquid,
    // nav-item-bestsellers.liquid) — without stripping them the label picks up
    // the hidden star-rating text and product excerpt alongside the title.
    var stripSelectors = ['svg', '.arrow', '.vegan', 'sup.new', '.label + svg', '.review_stars', '.description'];
    stripSelectors.forEach(function (sel) {
      var nodes = clone.querySelectorAll(sel);
      nodes.forEach(function (n) {
        n.parentNode && n.parentNode.removeChild(n);
      });
    });
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Walks up from the clicked element to find the menu_N <ul> it lives in,
   * returning the corresponding level label. Returns null if it's not inside
   * a recognised menu tier (e.g. it's in the top utility row).
   */
  function getLevel(el) {
    var levelMap = {
      menu_1: 'L1',
      menu_2: 'L2',
      menu_3: 'L3',
      menu_4: 'L4'
    };
    var node = el;
    while (node && node !== document.body) {
      if (node.classList) {
        for (var cls in levelMap) {
          if (node.classList.contains(cls)) {
            return levelMap[cls];
          }
        }
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Determines the origin bucket for the clicked item based on its
   * surrounding structural classes. Order matters — more specific checks
   * run first.
   */
  function getOrigin(el) {
    if (el.closest('.featured-banner')) {
      return 'nav_merchandising';
    }
    if (el.closest('.contently') || el.closest('.sliders')) {
      return 'nav_merchandising';
    }
    // Bestsellers product minicards — treated as merchandising per spec,
    // even though they sit directly in a menu_2 panel rather than a banner.
    if (el.closest('.minicard')) {
      return 'nav_merchandising';
    }
    if (el.closest('.utilities') || el.closest('.bh-nav_footer') || el.closest('.locale_selector')) {
      return 'nav_utility';
    }
    return 'mega_menu_panel';
  }

  function getTargetType(el) {
    var tag = el.tagName ? el.tagName.toLowerCase() : '';
    if (tag === 'a') return 'link';
    if (tag === 'button') return 'button';
    return tag || 'unknown';
  }

  /**
   * For buttons that control submenus, we read aria-expanded in bubble phase
   * (after the nav JS has already updated it) to determine whether this click
   * opened or closed the submenu.
   * For plain links and non-submenu buttons, interaction_type is 'click'.
   */
  function getInteractionType(el) {
    if (el.tagName.toLowerCase() === 'button' && el.hasAttribute('aria-expanded')) {
      // Bubble phase: aria-expanded has already been updated by nav JS
      // 'true' = just opened, 'false' = just closed
      return el.getAttribute('aria-expanded') === 'true' ? 'submenu_open' : 'submenu_close';
    }
    return 'click';
  }

  /**
   * Finds the actual interactive element to attribute the click to, since
   * clicks can land on a child span/svg/picture inside a nav link or button.
   */
  function resolveTarget(clickedEl) {
    return clickedEl.closest('a, button');
  }

  function handleClick(rootEl, fixedOrigin) {
    return function (event) {
      var target = resolveTarget(event.target);
      if (!target || !rootEl.contains(target)) {
        return;
      }

      var label = getCleanLabel(target);
      if (!label) {
        // Skip icon-only controls with no text (e.g. search toggle, cart icon)
        return;
      }

      pushEvent({
        label: label,
        interaction_type: getInteractionType(target),
        level: fixedOrigin ? null : getLevel(target),
        origin: fixedOrigin || getOrigin(target),
        target_type: getTargetType(target)
      });
    };
  }

  function init() {
    var mainMenu = document.getElementById('mainMenu');
    var siteMenu = document.querySelector('.site_menu, .bh-site_menu');

    if (mainMenu) {
      // Bubble phase (false) so aria-expanded has already been updated
      // by the nav JS before we read it in getInteractionType
      mainMenu.addEventListener('click', handleClick(mainMenu, null), false);
    }

    if (siteMenu) {
      // The desktop toggle row — tracked separately with fixed origin
      siteMenu.addEventListener('click', handleClick(siteMenu, 'top_bar_toggle'), false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();